import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import throttle from 'lodash/throttle';
import { BounceLoader } from 'halogenium';

import { LoadingIndicator } from 'components/ui/LoadingIndicator';
import { AccentColor } from 'helpers/AccentColorHelper';
import './Chat.css';
import { localize } from 'react-localize-redux';
import { colors } from 'helpers/ColorHelper';
import constants from 'helpers/constants';
import { Message } from './Message';
import TimestampDivider from './TimestampDivider';
import { getTranslatedString } from 'helpers/i18n';

const StyledConversationOuter = styled.div`
  height: 100%;
  overflow-y: scroll;
  margin-bottom: ${props => (props.messageCount > 0 ? '2rem' : '0')};
  transition: 0.15s all ease;
`;

const StyledConversationInner = styled.div`
  min-height: 100%;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const StyledNoMessagesContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 26rem;
  text-align: center;
`;

const StyledImg = styled.img`
  width: 100%;
  padding: 1rem 5rem;
`;

const StyledNoMessagesH3 = styled.h3`
  font-size: 1.5rem;
  font-family: 'Circular-Bold';
`;

const StyledNoMessagesP = styled.p`
  color: ${colors.MEDIUM_GRAY};
  padding: 0 5rem;
`;

const StyledMarkAsRead = styled.p`
  margin: 0;
  position: absolute;
  bottom: ${props => (props.isChatDrawer ? '14.5rem' : '8.5rem')};
  padding: ${props => (props.isChatDrawer ? '2px 0 2px 40px' : '2px 0 2px 26px')};
  width: 100%;
  background-color: #f1f1f1;
  margin-bottom: -3px;
  background-color: ${props => (props.isChatDrawer ? '#fff' : '#f1f1f1')};
  ${props =>
    !props.isChatDrawer &&
    css`
      @media (max-width: 768px) {
        bottom: ${props => (props.isChatDrawer ? '14.5rem' : '10.5rem')};
      }
    `};
`;

let Conversation = class Conversation extends Component {
  containerRef = React.createRef();
  lastMessageRef = React.createRef();
  lastMessageId;

  state = {
    markedAsRead: false,
  };

  handleScroll = e => {
    const scrollStrengthThreshold = 100;
    if (e.wheelDelta > scrollStrengthThreshold && this.containerRef.current.scrollTop < 10) {
      this.throttledFetch();
    }
  };

  throttledFetch = throttle(
    () => {
      this.props.fetchMessages(this.props.conversation.id, { isForMoreMessages: true });
    },
    2000,
    { trailing: false }
  );

  componentDidMount() {
    this.containerRef.current.addEventListener('wheel', this.handleScroll);
  }

  componentWillUnmount() {
    this.containerRef.current.removeEventListener('wheel', this.handleScroll);
  }

  componentDidUpdate() {
    if (this.lastMessageRef.current && this.lastMessageId !== this.lastMessageRef.current.id) {
      this.lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.lastMessageId = this.lastMessageRef.current.id;
    }

    if (this.state.markedAsRead && this.props.unreadMessageCount > 0) {
      this.setState({ markedAsRead: false });
    }
  }

  markMessagesAsRead = conversationId => {
    this.setState({ markedAsRead: true });
    this.props.markMessagesAsRead(conversationId);
  };

  render() {
    const {
      loadingBar,
      messages,
      conversation,
      loadingMostRecentMessages,
      translate,
      unreadMessageCount,
      isChatDrawer,
    } = this.props;

    const transformedMessages = tranformMessages(messages);
    const areMessagesPresent = !loadingMostRecentMessages && messages.length !== 0 && conversation;
    const isLoading = loadingBar;
    const hasNoMessages = !isLoading && messages.length === 0 && conversation;

    if (hasNoMessages) {
      this.lastMessageId = null;
    }

    return (
      <StyledConversationOuter messageCount={unreadMessageCount} ref={this.containerRef}>
        <StyledConversationInner>
          <LoadingIndicator
            shouldRender={isLoading}
            text={translate('messaging.loadingMessages')}
          />
          {hasNoMessages && (
            <StyledNoMessagesContainer>
              <StyledImg
                src="/emails_floating.svg"
                alt={translate('messages.noMessages').props.children}
              />
              <StyledNoMessagesH3>{translate('messages.noMessages')}</StyledNoMessagesH3>
              <StyledNoMessagesP>{translate('messages.noMessagesDescription')}</StyledNoMessagesP>
            </StyledNoMessagesContainer>
          )}
          {areMessagesPresent &&
            transformedMessages.map((item, i) => {
              if (item.type === 'TIME_DIVIDER') {
                return (
                  <TimestampDivider
                    key={`timeDivider-${i}`}
                    fromNow={item.fromNow}
                    time={item.time}
                  />
                );
              } else {
                return (
                  <Message
                    key={`message-${item.message_id}`}
                    message={item}
                    ref={i === transformedMessages.length - 1 ? this.lastMessageRef : null}
                    id={`message-${item.message_id}-${item.data.text}`}
                  />
                );
              }
            })}
        </StyledConversationInner>
        {this._renderMarkAsRead(unreadMessageCount, isChatDrawer)}
      </StyledConversationOuter>
    );
  }

  _renderMarkAsRead = (unreadMessageCount, isChatDrawer) => {
    if (!unreadMessageCount || unreadMessageCount === 0) return;
    if (this.state.markedAsRead) return;

    return (
      <StyledMarkAsRead isChatDrawer={isChatDrawer}>
        <button
          type="button"
          className="link-button"
          onClick={() => this.markMessagesAsRead(this.props.conversation.id)}
        >
          {this.props.translate('messaging.markAsRead')}
        </button>
      </StyledMarkAsRead>
    );
  };

  _renderLoadingMessages = () => {
    if (this.props.loadingMostRecentMessages && !this.p) return;

    return (
      <div
        className="row"
        style={{
          width: '100px',
          height: '100px',
          position: this.props.isLandlord ? '' : 'absolute',
          top: '-200px',
          bottom: '0',
          left: '0',
          right: '0',
          margin: 'auto',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            margin: 'auto',
            marginLeft: '21px',
            marginBottom: '10px',
          }}
        >
          <BounceLoader color={AccentColor()} style={{ margin: 'auto' }} />
        </div>
        <p style={{ fontFamily: 'Circular-Bold', marginTop: '5px' }}>
          {this.props.translate('messaging.loadingMessages')}
        </p>
      </div>
    );
  };
};

// transformMessages goes through the list of messages and returns a list of items to render
function tranformMessages(messages) {
  if (messages.length === 0) return messages;
  const transformedMessages = [];

  const firstMessage = { ...messages[0] };
  const secondMessage = { ...messages[1] };

  firstMessage.type = 'CHAT_BUBBLE';
  if (firstMessage.author_type === constants.userTypes.AGENT) {
    firstMessage.isFromSelf = true;
  }
  transformedMessages.push(timeDivider(firstMessage));

  if (!secondMessage) {
    firstMessage.isLast = true;
    transformedMessages.push(firstMessage);
    return transformedMessages;
  }

  if (
    !isFromSamePerson(firstMessage, secondMessage) ||
    hasBeenMoreThan(2, 'minutes')(firstMessage, secondMessage)
  ) {
    firstMessage.isLast = true;
  }
  transformedMessages.push(firstMessage);

  for (let i = 1; i < messages.length; i++) {
    transformMessage(messages[i - 1], messages[i], messages[i + 1], transformedMessages);
  }

  return transformedMessages;
}

// transformMessage adds extra information needed to render messages properly
// which includes whether the message is the first message sent in a really long time (needs to render timestamp divider)
// whether the message is the first message sent by a different user from the last message
// or the message is sent in rapid succession (not first message)
// or the message is the last message sent by the user (not in rapid succession)
function transformMessage(prev, curr, next, arr) {
  const transformedMessage = { ...curr };
  if (hasBeenMoreThan(30, 'minutes')(prev, transformedMessage)) {
    arr.push(timeDivider(transformedMessage));
  }

  transformedMessage.type = 'CHAT_BUBBLE';
  if (transformedMessage.author_type === constants.userTypes.AGENT) {
    transformedMessage.isFromSelf = true;
  }

  if (!next) {
    transformedMessage.isLast = true;
  } else {
    if (
      !isFromSamePerson(transformedMessage, next) ||
      hasBeenMoreThan(2, 'minutes')(transformedMessage, next)
    ) {
      transformedMessage.isLast = true;
    }
  }

  arr.push(transformedMessage);
}

function isFromSamePerson(prev, curr) {
  return prev.author_type === curr.author_type && prev.author_id === curr.author_id;
}

function hasBeenMoreThan(value, unit) {
  return (prev, curr) => {
    return moment(curr.date).diff(moment(prev.date), unit) > value;
  };
}

function timeDivider(message) {
  const date = moment(message.date, 'YYYY-MM-DD', window.localStorage.locale || 'en');

  return {
    type: 'TIME_DIVIDER',
    fromNow: date.format(
      'MMM Do, YYYY'
    ),
    time: `${getTranslatedString('at')} ${date.format(
      window.localStorage.locale === 'en' ? 'h:MM a' : 'HH:MM'
    )}`,
  };
}

export default localize()(Conversation);
